<template>
  <div
    class="flow-operator-form"
    @input="onFieldInput"
  >
    <!-- Loading overlay during WhatsApp resubmission or warning action -->
    <div
      v-if="isResubmitting || isWarningActionLoading"
      class="flow-operator-form__loading"
    >
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div class="flow-operator-form__header">
      <div
        class="flow-operator-form__icon"
        :class="`flow-operator-form__icon--${operator.kind}`"
      >
        <i :class="`far ${operator.icon}`" />
      </div>
      <div class="flow-operator-form__title">
        <h2>{{ t(operator.label) }}</h2>
        <span class="flow-operator-form__subtitle">
          {{ t(`flows.operatorKinds.${operator.kind}`) }}
        </span>
      </div>
      <Button
        icon="far fa-times"
        class="p-button-text p-button-rounded"
        @click="$emit('close')"
      />
    </div>

    <!-- Warning banners from operator validators -->
    <div
      v-if="formWarnings.length > 0"
      class="flow-operator-form__warnings"
    >
      <div
        v-for="warning in formWarnings"
        :key="warning.id"
        class="flow-operator-form__warning-bar"
        :class="`flow-operator-form__warning-bar--${warning.severity}`"
      >
        <div class="flow-operator-form__warning-content">
          <i
            v-if="warning.severity === 'warning'"
            class="fas fa-exclamation-triangle"
          />
          <i
            v-else-if="warning.severity === 'error'"
            class="fas fa-exclamation-circle"
          />
          <i
            v-else
            class="fas fa-info-circle"
          />
          <span>{{ t(warning.message, warning.messageParams || {}) }}</span>
        </div>

        <div
          v-if="warning.actions && warning.actions.length"
          class="flow-operator-form__warning-actions"
        >
          <Button
            v-for="action in warning.actions"
            :key="action.id"
            class="flow-operator-form__warning-action-btn"
            :disabled="isWarningActionLoading"
            @click="handleFormWarningAction(action.event)"
          >
            <i
              v-if="isWarningActionLoading"
              class="far fa-spinner-third fa-spin"
            />
            <i
              v-else-if="action.icon"
              :class="action.icon"
            />
            {{ t(action.label) }}
          </Button>
        </div>
      </div>
    </div>

    <div class="flow-operator-form__content">
      <component
        :is="operator.Form"
        v-model="localData"
        :form-validation="validationErrors"
        :campaign-id="campaignId"
        :campaign-type="campaignType"
        :shop-id="shopId"
        :operator-id="operatorId"
        @cancel="$emit('cancel')"
      />
    </div>

    <div class="flow-operator-form__footer">
      <Button
        :label="t('flows.operatorPanel.cancel')"
        class="p-button-text"
        @click="$emit('cancel')"
      />
      <Button
        :label="submitButtonLabel"
        class="p-button-success"
        :loading="isValidating || isResubmitting || isWarningActionLoading"
        :disabled="isValidating || isResubmitting || isWarningActionLoading"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref, watch, computed, onMounted, shallowRef, onUnmounted, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Message from 'primevue/message';
import type { FlowOperatorMetadata, NodeWarning, WarningValidationContext } from '@/types/flow-types';
import { useStore } from '@/store';
import { UserState } from '@/composables/User';
import { TemplateEditorState, resetWhatsappHashChanged } from '@/composables/template-editor/TemplateEditor';
import { WhatsappValidationStatus } from '@/composables/flows/useWhatsappTemplateValidation';

export default defineComponent({
  name: 'FlowOperatorForm',

  components: {
    Button,
    Message,
  },

  props: {
    operator: {
      type: Object as PropType<FlowOperatorMetadata>,
      required: true,
    },

    modelValue: {
      type: Object as PropType<any>,
      default: null,
    },

    isEditMode: {
      type: Boolean,
      default: false,
    },

    isReplaceMode: {
      type: Boolean,
      default: false,
    },

    operatorId: {
      type: String,
      default: undefined,
    },
  },

  emits: ['update:modelValue', 'submit', 'cancel', 'close', 'update-step'],

  setup(props, { emit, expose }) {
    const { t } = useI18n();
    const store = useStore();
    const localData = ref<any>(props.modelValue ?? props.operator.defaultData(undefined, store.getters['flowEditor/getActiveFlow']?.type));
    const validationErrors = shallowRef<any>({});
    const isValidating = ref(false);
    const isMounted = ref(true);
    // Save asked by the user, in progress
    const isSaving = ref(false);
    // Data sent to the panel, which closes: the save button keeps showing the save until then
    const isSubmitted = ref(false);

    onUnmounted(() => {
      isMounted.value = false;
    });

    // Get campaign info from store
    const activeFlow = computed(() => store.getters['flowEditor/getActiveFlow']);
    const campaignId = computed(() => activeFlow.value?.id ?? 0);
    const campaignType = computed(() => activeFlow.value?.type ?? '');
    const shopId = computed(() => UserState.activeShop?.id ?? 0);

    watch(localData, (newData) => {
      emit('update:modelValue', newData);
    }, { deep: true });

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localData.value) {
        localData.value = newValue;
      }
    });

    const submitButtonLabel = computed(() => {
      if (isSaving.value || isSubmitted.value) return t('flows.operatorPanel.saving');
      if (props.isEditMode) return t('flows.operatorPanel.saveStep');
      if (props.isReplaceMode) return t('flows.operatorPanel.replaceStep');
      return t('flows.operatorPanel.addStep');
    });

    // Run validation (used on mount and on submit)
    const runValidation = async (submitOnSuccess = false) => {
      if (!props.operator.validate) {
        if (submitOnSuccess) {
          await executeCallbackAndSubmit();
        }
        return;
      }

      isValidating.value = true;
      let submitted = false;

      try {
        const context = { shopId: shopId.value };
        const result = await props.operator.validate(localData.value, context);

        if (!isMounted.value) return;

        // Optimization: Don't set errors if we are about to submit and close
        // This avoids triggering prop updates on a component that will be unmounted
        if (!submitOnSuccess || !result.success) {
          validationErrors.value = result.validate || {};
        }

        if (result.success) {
          if (submitOnSuccess) {
            submitted = true;
            await executeCallbackAndSubmit();
          }
        } else {
          // Scroll to first error input after DOM update
          setTimeout(() => {
            const firstErrorElement = document.querySelector('.has-error');
            if (firstErrorElement) {
              firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        }
      } catch (error) {
        console.error('Validation error:', error);
        if (isMounted.value) {
          validationErrors.value = {};
        }
      } finally {
        if (isMounted.value && !submitted) {
          isValidating.value = false;
        }
      }
    };

    /**
     * Execute operator callback (if exists) and then submit
     * This is used for operators like WhatsApp that need to submit templates for validation
     */
    const executeCallbackAndSubmit = async () => {
      let dataToSubmit = localData.value;

      // Execute callback if operator has one (e.g., WhatsApp template submission)
      // Skip if:
      // 1. WhatsApp was already resubmitted after template editor close
      // 2. WhatsApp is already approved in edit mode (no changes made)
      const isWhatsappAlreadyApproved = props.operator.id === 'boxsendwhatsapp'
        && props.isEditMode
        && localData.value.template_validation_status === WhatsappValidationStatus.APPROVED;
      const shouldSkipCallback = props.operator.id === 'boxsendwhatsapp'
        && (whatsappAlreadyResubmitted.value || isWhatsappAlreadyApproved);
      if (props.operator.callback && !shouldSkipCallback) {
        try {
          const context = { shopId: shopId.value, skipValidation: false };
          const callbackResult = await props.operator.callback(localData.value, context);
          // Update local data with callback result only if it returns data (e.g., WhatsApp validation status)
          // Some callbacks like SendEmail return void and should not overwrite the data
          if (callbackResult !== undefined) {
            dataToSubmit = callbackResult;
            localData.value = dataToSubmit;
          }
        } catch (error) {
          console.error('Operator callback error:', error);
          // Continue with submission even if callback fails
        }
      }

      isSubmitted.value = true;
      emit('submit', dataToSubmit);
    };

    // BaseInputText, behind most text fields, only emits its value 500 ms after the last keystroke:
    // saving or checking for changes right after typing must wait for it, or the last edit is lost.
    const FIELD_EMIT_DELAY_MS = 500;
    let lastFieldInputAt = 0;

    const onFieldInput = () => {
      lastFieldInputAt = Date.now();
    };

    const settlePendingInput = async () => {
      const remaining = lastFieldInputAt + FIELD_EMIT_DELAY_MS - Date.now();
      if (remaining > 0) {
        await new Promise((resolve) => { setTimeout(resolve, remaining + 50); });
      }
      await nextTick();
    };

    // Validation started on mount (edit mode): a submit waits for it instead of being dropped
    let mountValidation: Promise<void> = Promise.resolve();

    // WhatsApp template resubmitted after the template editor, and banner actions ("Submit for validation")
    const isResubmitting = ref(false);
    const isWarningActionLoading = ref(false);

    // The save button is disabled meanwhile: a save asked from the panel's dialog is ignored too
    const isBusy = () => isSaving.value || isResubmitting.value || isWarningActionLoading.value;

    const handleSubmit = async () => {
      if (isBusy()) {
        return;
      }
      isSaving.value = true;
      // Keeps the button disabled while waiting
      isValidating.value = true;
      try {
        await mountValidation;
        // The mount validation clears the flag when it ends
        isValidating.value = true;
        await settlePendingInput();
        await runValidation(true);
      } finally {
        isSaving.value = false;
      }
    };

    // Used by FlowOperatorPanel to save or check for changes when the panel is being closed
    expose({ handleSubmit, settlePendingInput, isBusy });

    // Run validation when form opens (edit mode)
    onMounted(() => {
      if (props.isEditMode) {
        mountValidation = runValidation(false);
      }
    });

    // Form-level warnings computed from operator validators
    const formWarnings = ref<NodeWarning[]>([]);

    const computeFormWarnings = async () => {
      if (!props.operator.validators || !props.operator.validators.length) {
        formWarnings.value = [];
        return;
      }

      const context: WarningValidationContext = {
        operatorId: props.operator.id,
        data: localData.value,
        scenarioType: campaignType.value as any,
        parentIds: [],
        parentOperators: [],
        shopId: shopId.value,
      };

      const warnings: NodeWarning[] = [];
      for (const validator of props.operator.validators) {
        try {
          const result = await validator.validate(localData.value, context);
          if (result) {
            const items = Array.isArray(result) ? result : [result];
            // Only show warnings flagged for form display
            for (const w of items) {
              if (w.showInForm) {
                warnings.push(w);
              }
            }
          }
        } catch (e) {
          // Skip failed validators
        }
      }
      formWarnings.value = warnings;
    };

    const handleFormWarningAction = async (event: string) => {
      if (event === 'submit-whatsapp-validation') {
        if (!shopId.value || !localData.value?.template_display_selector_id) return;

        isWarningActionLoading.value = true;
        try {
          const { SubmitWhatsappValidation } = await import('@/composables/services/Whatsapp');
          const response = await SubmitWhatsappValidation(shopId.value, localData.value);

          let newStatus = WhatsappValidationStatus.ERROR;
          if (response.success && response.data && response.data.template) {
            const { status } = response.data.template;
            newStatus = ((WhatsappValidationStatus as any)[status] || WhatsappValidationStatus.PENDING) as WhatsappValidationStatus;
          }

          localData.value = {
            ...localData.value,
            template_validation_status: newStatus,
          };

          // Persist status change on the step immediately so it survives cancel
          if (props.operatorId) {
            emit('update-step', {
              stepId: props.operatorId,
              data: { template_validation_status: newStatus },
            });
          }
        } catch (error) {
          console.error('[WhatsApp Submit] Failed to submit for validation:', error);
          localData.value = {
            ...localData.value,
            template_validation_status: WhatsappValidationStatus.ERROR,
          };
        } finally {
          isWarningActionLoading.value = false;
        }
      }
    };

    // Recompute warnings when data changes (only in edit mode)
    watch(localData, () => {
      if (props.isEditMode) {
        computeFormWarnings();
      }
    }, { deep: true, immediate: true });

    // Track if WhatsApp was already resubmitted (to skip on form save)
    const whatsappAlreadyResubmitted = ref(false);

    // Watch for template editor close to check if WhatsApp hash changed
    const needRevalidation = computed(() => store.getters['liveEditor/getNeedRevalidation']);

    watch(() => TemplateEditorState.isTemplateEditorVisible, async (isVisible) => {
      // When template editor closes and hash changed, resubmit WhatsApp template
      if (!isVisible && needRevalidation.value && props.operator.id === 'boxsendwhatsapp') {
        store.commit('liveEditor/setNeedRevalidation', false);
        if (props.operator.callback) {
          isResubmitting.value = true;
          try {
            // Reset validation status to NOT_SUBMITTED before resubmitting
            localData.value.template_validation_status = WhatsappValidationStatus.NOT_SUBMITTED;

            // Call the callback to resubmit the template
            const context = { shopId: shopId.value, skipValidation: false };
            const result = await props.operator.callback(localData.value, context);

            if (result) {
              localData.value = result;
              emit('update:modelValue', result);
              // Mark as already resubmitted to skip on form save
              whatsappAlreadyResubmitted.value = true;
            }
          } catch (error) {
            console.error('WhatsApp resubmission error:', error);
          } finally {
            isResubmitting.value = false;
          }
        }

        resetWhatsappHashChanged();
      }
    });

    return {
      t,
      localData,
      validationErrors,
      isValidating,
      isResubmitting,
      handleSubmit,
      onFieldInput,
      submitButtonLabel,
      campaignId,
      campaignType,
      shopId,
      formWarnings,
      isWarningActionLoading,
      handleFormWarningAction,
    };
  },
});
</script>

<style lang="scss">
.flow-operator-form {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;

    .p-button {
      margin-left: auto;
      width: 28px !important;
      height: 28px !important;
      min-width: 28px !important;
      padding: 0 !important;
      color: #9ca3af !important;
      background: transparent !important;
      border: none !important;
      border-radius: 50% !important;

      &:hover {
        background: #f3f4f6 !important;
        color: #6b7280 !important;
      }

      &:focus {
        box-shadow: none !important;
      }
    }
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1rem;
    flex-shrink: 0;

    &--trigger {
      background: linear-gradient(135deg, #ffa31a, #ffb347);
    }

    &--action {
      background: linear-gradient(135deg, #7dd321, #9be15d);
    }

    &--filter {
      background: linear-gradient(135deg, #67b8f2, #8fc8ff);
    }
  }

  &__title {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    h2 {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
    }
  }

  &__subtitle {
    font-size: 0.75rem;
    color: #9ca3af;
  }

  &__warnings {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  &__warning-bar {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    margin: 0;
    font-size: 0.75rem;
    box-sizing: border-box;
    border-radius: 0.375rem;

    &--error {
      background: #fef2f2;
      color: #dc2626;

      i {
        color: #dc2626;
      }
    }

    &--warning {
      background: #fffbeb;
      color: #d97706;

      i {
        color: #d97706;
      }
    }

    &--info {
      background: #eff6ff;
      color: #2563eb;

      i {
        color: #2563eb;
      }
    }
  }

  &__warning-content {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;

    i {
      font-size: 0.875rem;
      flex-shrink: 0;
      margin-top: 0.125rem;
    }

    span {
      line-height: 1.3;
      color: #374151;
    }
  }

  &__warning-actions {
    display: flex;
    gap: 0.375rem;
    margin-top: 0.25rem;
    padding-left: 1.375rem; // Align with text
  }

  &__warning-action-btn.p-button {
    display: inline-flex !important;
    align-items: center !important;
    gap: 0.25rem !important;
    padding: 0.25rem 0.625rem !important;
    font-size: 0.6875rem !important;
    font-weight: 600 !important;
    border: none !important;
    border-radius: 0.25rem !important;
    cursor: pointer !important;
    background: rgba(0, 0, 0, 0.08) !important;
    color: #374151 !important;
    transition: background 0.15s ease !important;
    white-space: nowrap !important;
    box-shadow: none !important;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.15) !important;
    }

    &:focus {
      box-shadow: none !important;
    }

    &:disabled {
      cursor: not-allowed !important;
      opacity: 0.6 !important;
    }

    i {
      font-size: 0.625rem !important;
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  &__loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;

    i {
      font-size: 2rem;
      color: #6366f1;
    }
  }
}
</style>
